import React, { Component } from 'react'
import { PaginateResponse } from '../services/api.service'
import ResultLimiter from './ResultLimiter'
import { TableHorizontalProps } from './TableHorizontal'
import SearchPicker from './SearchPicker'
import Paginator from './Paginator'
import CardWrapper from './CardWrapper'
import ImagePopup from './Popup/image_popup'
import RadioGroup, { RadioGroupItem } from '../components/RadioGroup'
import { i18n } from '../i18n/i18n'
import Icon from './Icon'

export interface DataTableData<Data = {}>
    extends Pick<TableHorizontalProps<Data>, 'data'>,
        Omit<PaginateResponse<Data>, 'data'> {
}

interface DataTableProps extends DataTableData, TableHorizontalProps {
    limit: string
    search: string
    showSearch?: boolean
    selectedIds?: number[]
    onLimtChange: (limit: string) => void
    onSearch: (term: string) => void
    onPaginate: (page: number) => void
    onSelect?: (ids: number[]) => void
    onSortingChange: (sortBy: string) => void

    /**
     * When given, clicking a card image is handed to the parent instead of
     * opening this component's own popup — so the parent can show a preview
     * modal that knows which label it is (approval state, name, etc.).
     */
    onImagePreview?: (id: number, image: string) => void

    // Adding radios
    radioOptions?: RadioGroupItem[]
    useRadios?: boolean
    defaultRadioOption?: string
    radioChange?: (selectedOption: string) => void
    limitActions?: boolean
}

interface DataTableState {
    term: string
    isPopUpOpen: boolean
    selectedImage: string

    selectedOption: string
    sortDirection: string
}

export default class Gallery
  extends Component<DataTableProps, DataTableState> {
  constructor (props: DataTableProps) {
    super(props)
    this.state = {
      term: '',
      isPopUpOpen: false,
      selectedImage: '',
      selectedOption: this.props.defaultRadioOption ?? '',
      sortDirection: 'desc'
    }
    this.handleSearchChange = this.handleSearchChange.bind(this)
    this.handleSearch = this.handleSearch.bind(this)
    this.handlePopupClose = this.handlePopupClose.bind(this)
    this.handleImageClick = this.handleImageClick.bind(this)
    this.handleRadioChange = this.handleRadioChange.bind(this)
    this.handleSortingChange = this.handleSortingChange.bind(this)
  }

  get status () {
    const { from, to, total } = this.props
    if (!from || !to) return null
    return `Showing ${from} to ${to} of ${total} results`
  }

  handlePopupClose () {
    this.setState({ isPopUpOpen: false, selectedImage: '' })
  }

  handleImageClick (clickedImage: string, id?: number) {
    if (this.props.onImagePreview && id) {
      return this.props.onImagePreview(id, clickedImage)
    }
    this.setState({ isPopUpOpen: true, selectedImage: clickedImage })
  }

  handleSearchChange (name: string, value: string) {
    this.setState({ term: value })
  }

  handleSearch (name: string, term: string) {
    this.props.onSearch(term)
  }

  handleSortingChange () {
    const newSortDirection = this.state.sortDirection === 'asc' ? 'desc' : 'asc'
    this.setState({ sortDirection: newSortDirection }, () => {
      this.props.onSortingChange(newSortDirection)
    })
  }

  replaceStateTermWithProps () {
    this.setState({ term: this.props.search })
  }

  componentDidUpdate (props: DataTableProps) {
    if (this.props.search !== props.search) this.replaceStateTermWithProps()
  }

  componentDidMount () {
    this.replaceStateTermWithProps()
  }

  handleRadioChange (name: string) {
    this.setState({ selectedOption: name })
    if (this.props.radioChange) {
      this.props.radioChange(name)
    }
  }

  render () {
    return (
            <div className="block">
                <div className="flex flex-col md:flex-row md:justify-between">
                    <ResultLimiter value={this.props.limit} onChange={this.props.onLimtChange}/>
                    <div className="flex flex-col md:flex-row md:justify-between space-x-4">
                        {this.props.showSearch && (
                            <div className="w-full md:w-60">
                                <SearchPicker
                                    name="term"
                                    value={this.state.term}
                                    onChange={this.handleSearchChange}
                                    onSearch={this.handleSearch}/>
                            </div>
                        )}

                        <button
                            className="border border-solid border-primary text-xs py-2 px-5 h-9 flex gap-2"
                            onClick={this.handleSortingChange}
                        >
                            {i18n('Sort by date')}
                            <Icon name={`${this.state.sortDirection === 'asc' ? 'arrow-up' : 'arrow-down'}`}
                                  className={`${this.state.sortDirection === 'asc' ? 'ri-sort-asc' : 'ri-sort-desc'}`}
                                  size="extraSmall" color="dark"/>
                        </button>

                        {this.props.useRadios && (
                            <RadioGroup
                                label={i18n('Search by type')}
                                radios={this.props.radioOptions ?? []}
                                checked={this.state.selectedOption}
                                onChange={this.handleRadioChange}/>
                        )}

                        {!this.props.useRadios && !this.props.showSearch && (
                            <SearchPicker
                                name="term"
                                value={this.state.term}
                                onChange={this.handleSearchChange}
                                onSearch={this.handleSearch}/>
                        )}
                    </div>

                </div>
                <CardWrapper
                    limitActions={this.props.limitActions}
                    onImageClick={this.handleImageClick}
                    headers={this.props.headers}
                    data={this.props.data}
                    onActionClick={this.props.onActionClick}
                    onEditableChange={this.props.onEditableChange}
                    onSelect={this.props.onSelect}
                    selectedIds={this.props.selectedIds}
                    useRadios={this.props.useRadios}
                />
                <div className="flex flex-col lg:flex-row lg:justify-between lg:items-end">
                    <p className="text-secondary text-xs mb-1">
                        {this.status}
                    </p>
                    <Paginator
                        active={this.props.currentPage}
                        total={this.props.lastPage}
                        onPaginate={this.props.onPaginate}/>
                </div>
                <ImagePopup isOpen={this.state.isPopUpOpen} imageLink={this.state.selectedImage}
                            handleCloseClick={this.handlePopupClose}/>
            </div>
    )
  }
}
